/**
 * usePlaceIntentsMutation — toggle intents for a place via place_intents join table.
 * Syncs to place_core.intents automatically via DB trigger.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const MAX_INTENTS = 6;

interface ToggleParams {
  placeId: string;
  intentId: string;
  intentKey: string;
  enable: boolean;
  currentCount: number;
}

export function usePlaceIntentsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ placeId, intentId, enable, currentCount }: ToggleParams) => {
      if (enable && currentCount >= MAX_INTENTS) {
        throw new Error(`Max ${MAX_INTENTS} val tillåtna`);
      }

      if (enable) {
        const { error } = await supabase
          .from("place_intents")
          .insert({ place_id: placeId, intent_id: intentId });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("place_intents")
          .delete()
          .eq("place_id", placeId)
          .eq("intent_id", intentId);
        if (error) throw error;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["place-intents", variables.placeId] });
      queryClient.invalidateQueries({ queryKey: ["admin-place", variables.placeId] });
      // Invalidate matching queries since place_core.intents changes via trigger
      queryClient.invalidateQueries({ queryKey: ["match-rules-count"] });
      queryClient.invalidateQueries({ queryKey: ["tag-suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["debug-experience-match"] });
    },
    onError: (error) => {
      toast({
        title: "Kunde inte uppdatera",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
